// Temporal state stays separate from model inference so dropouts cannot repeat commands.
export function isOpenPalm(hand) {
  if (!hand) return false;
  if (hand.gesture === 'Thumb_Up' && hand.confidence >= .65) return false;
  const points = hand.landmarks;
  if (!points || points.length < 21 || points.some(p => !p || !Number.isFinite(p.x) || !Number.isFinite(p.y) || (p.z != null && !Number.isFinite(p.z)))) return false;
  const aspectRatio = hand.aspectRatio || 1;
  const distance = (a, b) => Math.hypot((a.x - b.x) * aspectRatio, a.y - b.y);
  const width = distance(points[5], points[17]);
  const palmLength = distance(points[0], points[9]);
  if (width < .001 || palmLength < .001) return false;
  for (const base of [5, 9, 13, 17]) {
    const a = points[base], b = points[base + 1], dip = points[base + 2], c = points[base + 3];
    const ux = (b.x - a.x) * aspectRatio, uy = b.y - a.y, vx = (c.x - b.x) * aspectRatio, vy = c.y - b.y;
    const uz = ((b.z ?? 0) - (a.z ?? 0)) * aspectRatio, vz = ((c.z ?? 0) - (b.z ?? 0)) * aspectRatio;
    const straightness = (ux * vx + uy * vy + uz * vz) / (Math.hypot(ux, uy, uz) * Math.hypot(vx, vy, vz) || 1);
    // A classifier label cannot substitute for four visibly upright fingers.
    // Require every joint to rise and reject sideways/downward open hands.
    const rise = palmLength * .035;
    if (b.y > a.y - rise || dip.y > b.y - rise || c.y > dip.y - rise
      || a.y - c.y <= Math.abs((c.x - a.x) * aspectRatio)
      || c.y > points[0].y - palmLength * .8 || straightness <= .65
      || distance(c, points[0]) <= distance(b, points[0]) * 1.08 || distance(a, c) <= width * .55) return false;
  }
  return true;
}

// Compare image-space landmarks from the same frame; depth is not shared by
// the face and hand models. Mouth width keeps the tolerance camera-distance aware.
export function isShush(hand, face) {
  const p = hand?.landmarks, mouth = face?.mouth;
  if (!p || p.length < 21 || !mouth || !(mouth.width > .01)) return false;
  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const extended = (base, straightness = .75, reach = 1.15) => {
    const a = p[base], b = p[base + 1], tip = p[base + 3];
    const ux = b.x - a.x, uy = b.y - a.y, vx = tip.x - b.x, vy = tip.y - b.y;
    return (ux * vx + uy * vy) / (Math.hypot(ux, uy) * Math.hypot(vx, vy) || 1) > straightness
      && distance(tip, p[0]) > distance(b, p[0]) * reach;
  };
  if (!extended(5, .45, 1.05) || [9, 13, 17].some(base => extended(base))) return false;
  const base = p[5], tip = p[8], dx = tip.x - base.x, dy = tip.y - base.y;
  if (dy >= 0 || Math.abs(dx) > -dy * .9) return false;
  const t = Math.max(0, Math.min(1, ((mouth.x - base.x) * dx + (mouth.y - base.y) * dy) / (dx * dx + dy * dy)));
  return distance(mouth, { x: base.x + t * dx, y: base.y + t * dy }) <= mouth.width * .6;
}

export class GestureEngine {
  constructor(dispatch, settings = {}, isPaused = () => true) {
    this.dispatch = dispatch; this.settings = settings; this.isPaused = isPaused;
    this.neutral = { yaw: 0, roll: 0, pitch: 0 }; this.reset();
  }
  reset() {
    this.resetHead(); this.palmSince = null; this.lastPalmAt = null; this.palmLatched = false; this.palmReleaseSince = null;
    this.shushSince = null; this.shushLatched = false; this.shushReleaseSince = null;
  }
  resetHead() {
    this.candidate = null; this.since = 0; this.latched = false; this.cooldownUntil = 0;
    this.headArmed = false; this.clearFaceCycles();
  }
  calibrate(face) {
    this.neutral = { yaw: face?.yaw || 0, roll: face?.roll || 0, pitch: Math.max(-12, Math.min(12, face?.pitch || 0)) };
    this.reset();
  }
  clearEyeCycle() { this.eyeIntent = null; this.eyeHeld = false; this.closedSince = null; this.eyesArmed = false; }
  clearMouthCycle() { this.mouthIntent = null; this.mouthHeld = false; this.mouthSince = null; this.mouthArmed = false; this.mouthReleaseSince = null; }
  clearWink() { this.winkSide = null; this.winkSince = null; this.winkNext = null; this.winkArmed = false; }
  clearFaceCycles() { this.clearEyeCycle(); this.clearMouthCycle(); this.clearWink(); }
  updateMouth(face, now) {
    // Lip separation supplements jawOpen, which varies substantially by face.
    // Hysteresis keeps a deliberate hold alive through small score fluctuations.
    const holding = this.mouthSince !== null;
    const score = face.mouthOpen || 0, gap = face.mouth?.openness || 0;
    const opened = score >= (holding ? .4 : .55) || gap >= (holding ? .26 : .38);
    const closed = score < .25 && gap < .12;
    if (closed) {
      this.mouthReleaseSince ??= now;
      if (!this.mouthHeld || now - this.mouthReleaseSince >= 120) {
        this.clearMouthCycle(); this.mouthArmed = true;
      }
    } else if (opened && this.mouthArmed) {
      this.mouthReleaseSince = null;
      if (this.mouthSince === null) { this.mouthSince = now; this.mouthIntent = this.isPaused() ? 'start' : 'stop'; }
      if (!this.mouthHeld && now - this.mouthSince >= (this.settings.eyeDelay ?? 350)) {
        this.mouthHeld = true;
        const paused = this.isPaused();
        if ((this.mouthIntent === 'start') === paused) this.dispatch({type:paused ? 'play' : 'pause',source:'mouth',label:paused ? 'Mouth held · playing' : 'Mouth held · paused'});
      }
    } else {
      this.mouthReleaseSince = null;
      if (!this.mouthHeld) { this.mouthSince = null; this.mouthIntent = null; }
    }
    return opened || this.mouthHeld;
  }
  facingCamera(face, eyes = false) {
    if (!face) return false;
    const pitch = face.pitch || 0, yaw = face.yaw - this.neutral.yaw;
    // Eye-look blendshapes also change during blinks/winks. Only head pose can
    // suspend controls; otherwise closing the eyes disarms the closure itself.
    return Math.abs(pitch - this.neutral.pitch) <= 18 && Math.abs(pitch) <= 32
      && Math.abs(yaw) <= (eyes ? 18 : 65);
  }
  trackingStatus(tracking) {
    if (tracking?.handIgnored && !tracking.ambiguous) return `hand-ignored-${tracking.handIgnored}`;
    if (tracking?.faceCount > 1 || tracking?.handCount > 1) {
      const kind = tracking.faceCount > 1 && tracking.handCount > 1 ? 'subjects' : tracking.faceCount > 1 ? 'faces' : 'hands';
      return `multiple-${kind}${tracking.ambiguous ? '' : '-focused'}`;
    }
    if (this.settings.handEnabled && tracking?.hand) return 'hand';
    if (!tracking?.face) return 'missing';
    return this.facingCamera(tracking.face) ? 'ready' : 'away';
  }
  update(tracking, now) {
    if (tracking?.ambiguous || tracking?.focusChanged) {
      this.resetHead(); this.palmSince = null; this.shushSince = null;
      // A competing subject is not a release. Preserve completed hand latches
      // until the gesture is visibly released, but discard unfinished holds.
      this.palmReleaseSince = null; this.shushReleaseSince = null;
      if (tracking.ambiguous) return;
    }
    const handIgnored = tracking?.handIgnored;
    if (handIgnored) {
      this.palmSince = null; this.shushSince = null;
      this.palmReleaseSince = null; this.shushReleaseSince = null;
    }
    const hand = this.settings.handEnabled && !handIgnored && tracking?.hand;
    const face = this.settings.headEnabled && tracking?.face;
    if (!handIgnored) {
      const shush = this.updateShush(hand, tracking?.face, now);
      this.updatePalm(shush || this.shushSince !== null ? null : hand, now);
    }
    // Keep the palm latch across short losses of tracking, including face/hand switches.
    if (hand || (this.lastPalmAt !== null && now - this.lastPalmAt < 400)) { this.resetHead(); return; }
    if (!this.facingCamera(face)) { this.resetHead(); return; }
    const yaw = face.yaw - this.neutral.yaw, roll = face.roll - this.neutral.roll;
    const neutral = Math.abs(yaw) < 12 && Math.abs(roll) < 11;
    if (neutral) this.headArmed = true;
    if (!this.headArmed) return;
    const closed = face.blinkLeft > .65 && face.blinkRight > .65;
    const open = face.blinkLeft < .3 && face.blinkRight < .3;
    const centered = this.facingCamera(face, true);
    const wink = face.blinkLeft > .65 && face.blinkRight < .3 ? 'Left' : face.blinkRight > .65 && face.blinkLeft < .3 ? 'Right' : null;
    if (this.settings.eyeEnabled && centered) {
      if (open) { this.clearWink(); this.winkArmed = true; }
      else if (wink) {
        this.clearEyeCycle(); this.clearMouthCycle();
        if (this.winkArmed) {
          if (!this.winkSide) { this.winkSide = wink; this.winkSince = now; this.winkNext = now + (this.settings.eyeDelay ?? 350); }
          if (this.winkSide !== wink) this.winkArmed = false;
          if (this.winkArmed && now >= this.winkNext) {
            this.winkNext = now + 500;
            this.dispatch({type:'skip', value:wink === 'Left' ? -2 : 2, source:'eyes', gesture:`wink${wink}`});
          }
        }
        this.hold(null,now); return;
      } else if (this.winkSide) { this.clearEyeCycle(); this.clearMouthCycle(); this.winkArmed = false; this.hold(null,now); return; }
    } else this.clearWink();
    if (this.settings.eyeEnabled && centered) {
      if (open) {
        const start = this.eyeHeld && this.eyeIntent === 'start' && this.isPaused();
        this.clearEyeCycle(); this.eyesArmed = true;
        if (start) {
          this.dispatch({ type: 'play', source: 'eyes', label: 'Eyes open · playing' });
        }
      } else if (closed && this.eyesArmed) {
        if (this.closedSince === null) {
          this.closedSince = now; this.eyeIntent = this.isPaused() ? 'start' : 'stop';
        }
        if (!this.eyeHeld && now - this.closedSince >= (this.settings.eyeDelay ?? 350)) {
          this.eyeHeld = true;
          if (this.eyeIntent === 'stop' && !this.isPaused()) this.dispatch({ type: 'pause', source: 'eyes', label: 'Eyes closed · paused' });
        }
      } else if (!closed && !this.eyeHeld) { this.closedSince = null; this.eyeIntent = null; }
    } else this.clearEyeCycle();
    if (closed || this.eyeHeld || this.closedSince !== null) {
      // A normal blink should not discard an otherwise deliberate mouth hold.
      if (this.eyeHeld) this.clearMouthCycle();
      this.hold(null, now); return;
    }
    if (centered && this.settings.mouthEnabled !== false) {
      if (this.updateMouth(face,now)) { this.hold(null,now); return; }
    } else this.clearMouthCycle();
    let key = null;
    // The model's positive roll is the user's left tilt (mirrored camera preview).
    if (Math.abs(roll) > 19) key = roll > 0 ? 'tiltLeft' : 'tiltRight';
    else if (Math.abs(yaw) > 23) key = yaw < 0 ? 'left' : 'right';
    if (key || neutral) this.hold(key, now);
  }
  updateShush(hand, face, now) {
    const active = this.facingCamera(face) && Math.abs(face.yaw - this.neutral.yaw) <= 30 && isShush(hand, face);
    if (!active) {
      // Full tracking loss is not proof of release. A visible face with the
      // hand lowered is a release, but brief hand dropouts cannot rearm it.
      if (!face) { this.shushReleaseSince = null; this.shushSince = null; }
      else {
        this.shushReleaseSince ??= now;
        if (now - this.shushReleaseSince >= 150) this.shushSince = null;
        if (now - this.shushReleaseSince >= 400) this.shushLatched = false;
      }
      return false;
    }
    if (this.shushReleaseSince !== null && now - this.shushReleaseSince >= 150) this.shushSince = null;
    if (this.shushReleaseSince !== null && now - this.shushReleaseSince >= 400) this.shushLatched = false;
    this.shushReleaseSince = null;
    this.shushSince ??= now;
    if (!this.shushLatched && now - this.shushSince >= (this.settings.eyeDelay ?? 350)) {
      this.shushLatched = true;
      this.dispatch({ type: 'mute', source: 'gesture', gesture: 'shush' });
    }
    return true;
  }
  updatePalm(hand, now) {
    if (!isOpenPalm(hand)) {
      this.palmSince = null;
      this.palmReleaseSince ??= now;
      if (now - this.palmReleaseSince >= 400) {
        this.palmLatched = false; this.palmSince = null;
      }
      return;
    }
    if (this.palmReleaseSince !== null && now - this.palmReleaseSince >= 400) { this.palmSince = now; this.palmLatched = false; }
    this.palmReleaseSince = null;
    this.lastPalmAt = now; this.palmSince ??= now;
    if (!this.palmLatched && now - this.palmSince >= 200) {
      this.palmLatched = true;
      this.dispatch({ type: 'toggle', source: 'gesture', gesture: 'palm' });
    }
  }
  hold(key, now) {
    if (!key) { this.candidate = null; this.latched = false; return; }
    if (this.latched || now < this.cooldownUntil) return;
    if (this.candidate !== key) { this.candidate = key; this.since = now; return; }
    if (now - this.since < 450) return;
    this.latched = true; this.cooldownUntil = now + 450;
    const command = { left: { type: 'look-left' }, right: { type: 'next' }, tiltLeft: { type: 'skip', value: -10 }, tiltRight: { type: 'skip', value: 10 } }[key];
    if (command) this.dispatch({ ...command, source: 'gesture', gesture: key });
  }
}
